import React, { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../utils/axiosInstance";
import { motion, AnimatePresence } from "framer-motion";
import * as Icon from "react-bootstrap-icons";
import { format, parseISO } from "date-fns";
import { useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const fmtDate = (d) => {
  try { return format(parseISO(d), "dd MMM yyyy"); } catch { return d || "—"; }
};

const statutColor = (s) => {
  if (!s) return "#94a3b8";
  if (s.includes("Yemejwe"))    return "#1a7a4a";
  if (s.includes("Akagari"))    return "#003580";
  if (s.includes("Umudugudu")) return "#b45309";
  if (s.includes("Yasubijwe")) return "#b91c1c";
  return "#64748b";
};

/* ── Signature + Forward Modal ── */
const SignForwardModal = ({ raporo, onClose, onDone }) => {
  const [step, setStep]     = useState("sign"); // sign | forwarding | done
  const [signing, setSigning] = useState(false);
  const [error, setError]   = useState(null);
  const sigPadRef           = useRef(null);

  const handleSign = async () => {
    if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
      setError("Nyamuneka shyiraho umukono mbere yo kubika.");
      return;
    }
    setSigning(true);
    setError(null);
    try {
      // Safe canvas export — avoids getTrimmedCanvas() scaling bug
      const raw = sigPadRef.current.getCanvas();
      const out = document.createElement("canvas");
      out.width = raw.width;
      out.height = raw.height;
      const ctx = out.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, out.width, out.height);
      ctx.drawImage(raw, 0, 0);
      const sigData = out.toDataURL("image/jpeg", 0.6);

      if (!sigData || sigData.length < 1000) {
        setError("Umukono ntabwo wabashije gufatwa. Ongera ugerageze.");
        setSigning(false);
        return;
      }

      // Get signer name from token
      let signerName = "Akagari";
      try {
        const p = JSON.parse(atob(localStorage.getItem("token").split(".")[1]));
        signerName = p.fullname || p.name || "Akagari";
      } catch { /* ignore */ }

      // Step 1: Save receiver signature
      await axiosInstance.post(`/raporo/${raporo.raporo_id}/sign-receiver`, {
        signature_image: sigData,
        receiver_name: signerName,
        receiver_role: "Akagari",
        receiver_level: "Akagari",
      });

      setStep("forwarding");

      // Step 2: Forward to Akagari
      await axiosInstance.post(`/raporo/${raporo.raporo_id}/send`);

      setStep("done");
      toast.success("✅ Umukono wabitswe kandi raporo yoherejwe kuri Akagari!");
      setTimeout(() => { onDone(); onClose(); }, 1500);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        setError("Igihe cya konti yawe kirangiye. Injira nanone.");
        localStorage.removeItem("token");
        setTimeout(() => { window.location.href = "/akagari"; }, 1500);
      } else {
        setError(err?.response?.data?.error || "Habaye ikibazo. Gerageza nanone.");
      }
      setStep("sign");
    } finally {
      setSigning(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 2000,
        background: "rgba(15,23,42,0.75)", backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem",
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 16 }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 24, padding: "2rem",
          width: "100%", maxWidth: 500,
          boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
        }}
      >
        {step === "done" ? (
          <div style={{ textAlign: "center", padding: "1.5rem 0" }}>
            <div style={{ fontSize: "3.5rem", marginBottom: "1rem" }}>✅</div>
            <h5 style={{ fontWeight: 700, color: "#15803d", margin: "0 0 0.5rem" }}>Byagenze neza!</h5>
            <p style={{ color: "#64748b", fontSize: "0.9rem", margin: 0 }}>
              Raporo yashyizweho umukono kandi yoherejwe kuri Akagari.
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: "1.5rem" }}>
              <div style={{
                width: 44, height: 44, borderRadius: 14,
                background: "linear-gradient(135deg,#0f766e,#0d9488)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Icon.PenFill size={20} color="#fff" />
              </div>
              <div>
                <h5 style={{ fontWeight: 800, margin: 0, color: "#1e293b", fontSize: "1rem" }}>
                  Shyiraho Umukono w'Uwakiriye
                </h5>
                <p style={{ margin: 0, fontSize: "0.78rem", color: "#64748b" }}>
                  Umudugudu → Akagari
                </p>
              </div>
              <button
                onClick={onClose}
                style={{
                  marginLeft: "auto", background: "none", border: "none",
                  cursor: "pointer", color: "#94a3b8", fontSize: "1.4rem", lineHeight: 1,
                }}
              >✕</button>
            </div>

            {/* Report info */}
            <div style={{
              background: "#f8fafc", borderRadius: 12, padding: "0.9rem 1rem",
              marginBottom: "1.25rem", border: "1px solid #e2e8f0",
            }}>
              <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>
                Raporo
              </div>
              <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "0.95rem" }}>
                {raporo.umutwe_wa_raporo || "Raporo"}
              </div>
              <div style={{ fontSize: "0.82rem", color: "#64748b", marginTop: 4 }}>
                <Icon.PersonFill size={11} /> {raporo.yakozwe_na || raporo.umudugudu_name || "—"} &nbsp;·&nbsp;
                <Icon.CalendarFill size={11} /> {fmtDate(raporo.itariki_ya_raporo)}
              </div>
            </div>

            {/* Signature pad */}
            <div style={{ marginBottom: "1rem" }}>
              <div style={{
                fontSize: "0.72rem", fontWeight: 700, color: "#0f766e",
                textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8,
              }}>
                Shyiraho Umukono Wawe
              </div>
              <div style={{
                border: "2px dashed #0f766e", borderRadius: 12,
                background: "#f0fdf4", overflow: "hidden", position: "relative",
              }}>
                <SignatureCanvas
                  ref={sigPadRef}
                  penColor="#0f766e"
                  canvasProps={{
                    width: 460,
                    height: 160,
                    style: { width: "100%", height: 160, cursor: "crosshair", touchAction: "none" },
                  }}
                />
                <div style={{
                  position: "absolute", top: "50%", left: "50%",
                  transform: "translate(-50%,-50%)",
                  fontSize: "0.75rem", color: "#94a3b8", pointerEvents: "none",
                  userSelect: "none", whiteSpace: "nowrap",
                }}>
                  Shyiraho umukono hano ✍️
                </div>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{
                background: "#fee2e2", border: "1px solid #fecaca", borderRadius: 10,
                padding: "0.75rem 1rem", marginBottom: "1rem",
                fontSize: "0.85rem", color: "#dc2626", fontWeight: 600,
              }}>
                ⚠️ {error}
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => sigPadRef.current?.clear()}
                style={{
                  flex: 1, padding: "0.7rem", border: "2px solid #e2e8f0",
                  borderRadius: 12, background: "#f8fafc", color: "#475569",
                  fontWeight: 700, cursor: "pointer", fontSize: "0.85rem",
                }}
              >
                <Icon.EraserFill size={13} /> Siba
              </button>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: "0.7rem", border: "2px solid #e2e8f0",
                  borderRadius: 12, background: "#fff", color: "#64748b",
                  fontWeight: 700, cursor: "pointer", fontSize: "0.85rem",
                }}
              >
                Reka
              </button>
              <button
                onClick={handleSign}
                disabled={signing || step === "forwarding"}
                style={{
                  flex: 2, padding: "0.7rem", border: "none", borderRadius: 12,
                  background: (signing || step === "forwarding") ? "#94a3b8" : "linear-gradient(135deg,#0f766e,#0d9488)",
                  color: "#fff", fontWeight: 800,
                  cursor: (signing || step === "forwarding") ? "not-allowed" : "pointer",
                  fontSize: "0.85rem", display: "flex", alignItems: "center",
                  justifyContent: "center", gap: 8,
                }}
              >
                {signing && (
                  <><span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /> Gusinyisha...</>
                )}
                {step === "forwarding" && (
                  <><span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /> Kohereza...</>
                )}
                {!signing && step === "sign" && (
                  <><Icon.PenFill size={14} /> Sinyisha → Ohereza Akagari</>
                )}
              </button>
            </div>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
          </>
        )}
      </motion.div>
    </div>
  );
};

/* ── Main Component ── */
const GusomaRaporo = () => {
  const [raporo, setRaporo]           = useState([]);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState("");
  const [modalRaporo, setModalRaporo] = useState(null);
  const navigate = useNavigate();

  const fetchRaporo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/raporo_akagari");
      setRaporo(res.data || []);
    } catch {
      toast.error("Ntibyashobotse kubona raporo.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRaporo(); }, [fetchRaporo]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Ushaka gusiba iyi raporo?")) return;
    try {
      await axiosInstance.delete(`/raporo/${id}`);
      setRaporo(prev => prev.filter(r => r.raporo_id !== id));
      toast.success("Raporo yasibwe neza!");
    } catch {
      toast.error("Ntibyashobotse gusiba raporo.");
    }
  };

  const filtered = raporo.filter((r) => {
    const q = search.toLowerCase();
    return (
      (r.umutwe_wa_raporo || "").toLowerCase().includes(q) ||
      (r.yakozwe_na       || "").toLowerCase().includes(q) ||
      (r.umudugudu_name   || "").toLowerCase().includes(q)
    );
  });

  return (
    <div style={{ minHeight: "100%" }}>
      <ToastContainer position="top-right" autoClose={3500} hideProgressBar={false} theme="colored" />
      <style>{`
        .gr-card { padding: 1.25rem 1.5rem !important; }
        @media (max-width: 575px) {
          .gr-card { padding: 1rem !important; }
          /* buttons get their own full-width row; the sign button stretches */
          .gr-actions { width: 100%; padding-top: 10px; border-top: 1px solid var(--border); }
          .gr-actions > button[title^="Shyiraho"] { flex: 1 1 auto; justify-content: center; }
        }
        /* status pills never break mid-word */
        .gr-card .gr-pill { white-space: nowrap; display: inline-flex; align-items: center; gap: 3px; line-height: 1.6; }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.75rem", marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: "var(--primary-soft)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon.FileEarmarkText size={24} color="var(--primary)" />
          </div>
          <div>
            <h5 style={{ fontWeight: 700, margin: 0, color: "var(--text)" }}>Gusoma Raporo</h5>
            <small style={{ color: "var(--text-soft)" }}>{raporo.length} raporo zoherejwe n'Imidugudu</small>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", flex: "1 1 320px", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", alignItems: "center", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "0 12px", flex: "1 1 220px", minWidth: 0 }}>
            <Icon.Search size={16} color="var(--text-soft)" />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Shakisha raporo..."
              style={{ border: "none", background: "transparent", outline: "none", padding: "0.6rem 0.5rem", fontSize: "0.88rem", color: "var(--text)", width: "100%" }}
            />
            {search && (
              <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-soft)", padding: 0 }}>
                <Icon.XCircleFill size={14} />
              </button>
            )}
          </div>
          <button
            onClick={fetchRaporo}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "0.6rem 1rem", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 10, cursor: "pointer", fontSize: "0.85rem", color: "var(--text-soft)", fontWeight: 600 }}
          >
            <Icon.ArrowClockwise size={15} /> Vugurura
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "4rem 0" }}>
          <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} />
          <p style={{ marginTop: "1rem", color: "var(--text-soft)" }}>Raporo zirimo gutegurwa...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "5rem 2rem" }}>
          <div style={{ width: 100, height: 100, borderRadius: "50%", background: "var(--primary-soft)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.5rem" }}>
            <Icon.FileEarmarkX size={48} color="var(--primary)" />
          </div>
          <h5 style={{ fontWeight: 700, color: "var(--text)" }}>
            {search ? "Nta raporo ibonetse" : "Nta raporo yoherejwe"}
          </h5>
          <p style={{ color: "var(--text-soft)", maxWidth: 380, margin: "0 auto" }}>
            {search ? "Gerageza amagambo atandukanye." : "Imidugudu ntiyohereje raporo narimwe."}
          </p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map((r, i) => {
            const senderSigned   = !!(r.sender_sig_date || r.sender_sig_image || r.signed_at || r.signature_image_snapshot || r.sig_image);
            const receiverSigned = !!(r.receiver_sig_date || r.receiver_sig_image || r.receiver_signed_at || r.receiver_signature_image);
            const canSignForward = !receiverSigned;

            return (
              <motion.div
                key={r.raporo_id}
                className="gr-card"
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                style={{
                  background: "var(--surface)", border: "1px solid var(--border)",
                  borderRadius: 14, cursor: "pointer",
                  transition: "all 0.2s ease", boxShadow: "0 2px 8px rgba(0,53,128,0.06)",
                }}
                onClick={() => navigate(`/view-report/${r.raporo_id}`)}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,53,128,0.12)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,53,128,0.06)"}
              >
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>

                  {/* Left info — basis 260px: on phones the buttons wrap below instead of squeezing this */}
                  <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
                      <h6 style={{ fontWeight: 700, margin: 0, color: "var(--text)", fontSize: "1rem", flexBasis: "100%", overflowWrap: "anywhere" }}>
                        {r.umutwe_wa_raporo || "Raporo"}
                      </h6>
                      {r.statut && (
                        <span className="gr-pill" style={{ fontSize: "0.72rem", fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: `${statutColor(r.statut)}18`, color: statutColor(r.statut), border: `1px solid ${statutColor(r.statut)}30` }}>
                          {r.statut}
                        </span>
                      )}
                      {senderSigned
                        ? <span className="gr-pill" style={{ fontSize: "0.68rem", fontWeight: 700, padding: "2px 9px", borderRadius: 20, background: "#dcfce7", color: "#15803d", border: "1px solid #bbf7d0" }}>✅ Uwatanze Yasinyye</span>
                        : <span className="gr-pill" style={{ fontSize: "0.68rem", fontWeight: 700, padding: "2px 9px", borderRadius: 20, background: "#fef3c7", color: "#b45309", border: "1px solid #fde68a" }}>⏳ Uwatanze Ntiyasinyye</span>
                      }
                      {receiverSigned
                        ? <span className="gr-pill" style={{ fontSize: "0.68rem", fontWeight: 700, padding: "2px 9px", borderRadius: 20, background: "#dbeafe", color: "#1d4ed8", border: "1px solid #bfdbfe" }}>✅ Umudugudu Yasinyye</span>
                        : <span className="gr-pill" style={{ fontSize: "0.68rem", fontWeight: 700, padding: "2px 9px", borderRadius: 20, background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0" }}>⏳ Umudugudu Ntiyasinyye</span>
                      }
                    </div>

                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                      <span style={{ fontSize: "0.82rem", color: "var(--text-soft)", display: "flex", alignItems: "center", gap: 4 }}>
                        <Icon.PersonFill size={12} /> {r.yakozwe_na || r.umudugudu_name || "—"}
                      </span>
                      <span style={{ fontSize: "0.82rem", color: "var(--text-soft)", display: "flex", alignItems: "center", gap: 4 }}>
                        <Icon.GeoAltFill size={12} /> {r.umudugudu_name || "—"}
                      </span>
                      <span style={{ fontSize: "0.82rem", color: "var(--text-soft)", display: "flex", alignItems: "center", gap: 4 }}>
                        <Icon.CalendarFill size={12} /> {fmtDate(r.itariki_ya_raporo)}
                      </span>
                    </div>

                    {r.ibisobanuro && (
                      <p style={{ margin: "8px 0 0", fontSize: "0.83rem", color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }}>
                        {r.ibisobanuro}
                      </p>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="gr-actions" style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>

                    {/* View */}
                    <button
                      onClick={e => { e.stopPropagation(); navigate(`/view-report/${r.raporo_id}`); }}
                      title="Soma Raporo"
                      style={{ width: 36, height: 36, borderRadius: 10, border: "1px solid var(--border)", background: "var(--primary-soft)", color: "var(--primary)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <Icon.EyeFill size={15} />
                    </button>

                    {/* Sign + Forward button — open inline modal for Akagari */}
                    {canSignForward && (
                      <button
                        onClick={e => { e.stopPropagation(); setModalRaporo(r); }}
                        title="Shyiraho Umukono w'Uwakiriye (Umudugudu)"
                        style={{
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "0 14px", height: 36, borderRadius: 10,
                          border: "1px solid rgba(15,118,110,0.3)",
                          background: "linear-gradient(135deg,#0f766e,#0d9488)",
                          color: "#fff", cursor: "pointer", fontWeight: 700,
                          fontSize: "0.78rem", whiteSpace: "nowrap",
                          boxShadow: "0 2px 8px rgba(15,118,110,0.3)",
                          transition: "transform 0.15s, box-shadow 0.15s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.05)"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(15,118,110,0.45)"; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(15,118,110,0.3)"; }}
                      >
                        <Icon.PenFill size={13} /> Shyiraho Umukono w'Uwakiriye
                      </button>
                    )}

                    {/* Already forwarded badge */}
                    {receiverSigned && (
                      <span style={{ display: "flex", alignItems: "center", gap: 5, padding: "0 12px", height: 36, borderRadius: 10, background: "#dcfce7", color: "#15803d", border: "1px solid #bbf7d0", fontSize: "0.78rem", fontWeight: 700, whiteSpace: "nowrap" }}>
                        <Icon.CheckCircleFill size={13} /> Yoherejwe
                      </span>
                    )}

                    {/* Download */}
                    {r.attachment && (
                      <a
                        href={r.attachment} download onClick={e => e.stopPropagation()}
                        title="Download"
                        style={{ width: 36, height: 36, borderRadius: 10, border: "1px solid var(--border)", background: "rgba(26,122,74,0.08)", color: "#1a7a4a", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}
                      >
                        <Icon.Download size={15} />
                      </a>
                    )}

                    {/* Delete */}
                    <button
                      onClick={e => handleDelete(e, r.raporo_id)}
                      title="Siba Raporo"
                      style={{ width: 36, height: 36, borderRadius: 10, border: "1px solid rgba(185,28,28,0.2)", background: "rgba(185,28,28,0.06)", color: "#b91c1c", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <Icon.TrashFill size={15} />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Sign + Forward Modal */}
      <AnimatePresence>
        {modalRaporo && (
          <SignForwardModal
            raporo={modalRaporo}
            onClose={() => setModalRaporo(null)}
            onDone={() => fetchRaporo()}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default GusomaRaporo;
